import ScoreBadge from "@/components/feature/ScoreBadge";
import StatusBadge from "@/components/feature/StatusBadge";
import { formatFollowers, type Prospect } from "@/pages/prospectar/components/ProspectCard";

const platformIcon: Record<string, string> = {
  Instagram: "ri-instagram-line",
  TikTok: "ri-tiktok-line",
  YouTube: "ri-youtube-line",
  LinkedIn: "ri-linkedin-line",
  WhatsApp: "ri-whatsapp-line",
  Facebook: "ri-facebook-circle-line",
};

const followTone: Record<string, string> = {
  HOY: "bg-accent-500/15 text-accent-700",
  MAÑANA: "bg-primary-500/15 text-primary-700",
};

type ContactListViewProps = {
  contacts: Prospect[];
  onOpen: (c: Prospect) => void;
};

export default function ContactListView({ contacts, onOpen }: ContactListViewProps) {
  if (contacts.length === 0) {
    return (
      <div className="bg-background-100 border border-dashed border-background-300 rounded-2xl p-10 text-center">
        <i className="ri-contacts-book-line text-4xl text-foreground-400"></i>
        <p className="text-foreground-600 mt-3">Aún no tienes contactos en esta etapa.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {contacts.map((c) => (
        <article
          key={c.id}
          className="bg-background-100 border border-background-200 rounded-2xl p-4 flex items-start gap-4 hover:border-primary-400 cursor-pointer transition-colors"
          onClick={() => onOpen(c)}
        >
          <img
            src={c.photo}
            alt={`Foto de ${c.name}`}
            className="w-14 h-14 rounded-2xl object-cover object-top border border-background-200 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-foreground-950 truncate">{c.name}</h3>
                <div className="flex items-center gap-2 text-xs text-foreground-600 mt-0.5">
                  <span className="inline-flex items-center gap-1">
                    <i className={platformIcon[c.platform] ?? "ri-user-line"}></i>
                    {c.username}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-group-line"></i> {formatFollowers(c.followers)}
                  </span>
                </div>
              </div>
              <ScoreBadge score={c.score} />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <StatusBadge status={c.status} />
              {c.needsHuman ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-accent-500/15 text-accent-700" title={c.needsHumanReason ?? ""}>
                  <i className="ri-alarm-warning-line"></i> Necesita intervención
                </span>
              ) : c.handledBy === "ai" && c.platform === "WhatsApp" ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-primary-500/10 text-primary-700">
                  <i className="ri-robot-2-line"></i> Lo atiende el agente
                </span>
              ) : null}
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${
                  followTone[c.followUp] ?? "bg-background-100 text-foreground-700"
                }`}
              >
                <i className="ri-calendar-line"></i> {c.followUp}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm text-foreground-700">
              <i className="ri-arrow-right-circle-line text-primary-500"></i>
              <span className="truncate">{c.nextAction}</span>
            </div>
          </div>
        </article>
      ))}
    </div>
  );
}